import { SvgIcon } from "@tql/ui-components-react/kendo/common";
import type { GridCustomHeaderCellProps } from "@tql/ui-components-react/kendo/grid";
import { Tooltip } from "@tql/ui-components-react/kendo/tooltip";
import { infoCircle } from "@tql/ui-components-react/untitled/icons";

import * as styles from "./header-cell.module.css";

type HeaderCellProps = Partial<GridCustomHeaderCellProps> & {
	title: string;
	tooltipText: string;
};

// Map Kendo's camelCased ARIA props (and strip non-DOM keys) so the
// resulting `<th>` keeps valid, accessible attributes when we replace
// Kendo's default header cell rendering.
const transformThProps = (
	thProps: GridCustomHeaderCellProps["thProps"] | undefined,
): Record<string, unknown> => {
	if (!thProps) return {};
	const result: Record<string, unknown> = {};
	for (const [key, value] of Object.entries(thProps)) {
		switch (key) {
			case "ariaSort": {
				result["aria-sort"] = value;
				break;
			}
			case "ariaColumnIndex": {
				result["aria-colindex"] = value;
				break;
			}
			case "ariaSelected": {
				result["aria-selected"] = value;
				break;
			}
			case "ariaDescription": {
				result["aria-description"] = value;
				break;
			}
			case "ariaLabel": {
				result["aria-label"] = value;
				break;
			}
			case "columnId": {
				// Non-DOM attribute used internally by Kendo; drop it.
				break;
			}
			default: {
				result[key] = value;
				break;
			}
		}
	}
	return result;
};

export const HeaderCell = ({
	title,
	tooltipText,
	thProps,
	onClick,
	children,
}: HeaderCellProps) => {
	const transformedThProps = transformThProps(thProps);
	// Kendo types `onClick` loosely as `any` on `HeaderCellBaseProps`; narrow
	// it for the DOM event handler signature. The same handler accepts both
	// mouse and keyboard events so the sort link stays keyboard-accessible.
	const handleActivate = onClick as
		| ((
				event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
		  ) => void)
		| undefined;
	const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
		if (event.key !== "Enter" && event.key !== " ") return;
		event.preventDefault();
		handleActivate?.(event);
	};

	return (
		<th {...transformedThProps}>
			<Tooltip
				anchorElement={"target"}
				position={"left"}
				style={{ width: "250px" }}
			>
				<span className={`k-cell-inner ${styles.headerCell}`}>
					<span
						className={"k-link"}
						onClick={handleActivate}
						onKeyDown={handleKeyDown}
						role={"button"}
						tabIndex={-1}
					>
						<span className={`k-column-title ${styles.title}`}>{title}</span>
						{children}
					</span>
					<span
						aria-label={tooltipText}
						className={styles.iconWrapper}
						role={"img"}
						title={tooltipText}
					>
						<SvgIcon
							icon={infoCircle}
							size={"large"}
							style={{ pointerEvents: "none" }}
						/>
					</span>
				</span>
			</Tooltip>
		</th>
	);
};
